    import React, { useState, useRef, useEffect } from 'react';
    import usePDA1100_R01Store from "./usePDA1100_R01Store";
    import { usePDA1100_R01Content } from "./PDA1100_R01Content";

    /**
     * PDA1100_R01 자재 저장위치 등록 폼
     */
    const PDA1100_R01Info = () => {
    const { 
        scanValue, 
        setScanValue, 
        newLocationCd,
        setNewLocationCd,
        data,
        loading,
    } = usePDA1100_R01Store();

    const { 
        handleSave,
        handleCancel,
        handleScanEnter,
        handleLocationEnter,
    } = usePDA1100_R01Content();
    
    // 키보드 제어
    const [scanKeyboardEnabled, setScanKeyboardEnabled] = useState(false);
    const [locationKeyboardEnabled, setLocationKeyboardEnabled] = useState(false);
    const [lastTapTime, setLastTapTime] = useState(0);
    
    const scanInputRef = useRef<HTMLInputElement>(null);
    const locationInputRef = useRef<HTMLInputElement>(null);

    // 초기 포커스
    useEffect(() => {
        scanInputRef.current?.focus();
    }, []);

    // 스캔 성공 후 새 저장위치 필드로 포커스
    useEffect(() => {
        if (data) {
        locationInputRef.current?.focus();
        }
    }, [data]);

    // 저장/취소 후 스캔 필드로 포커스
    useEffect(() => {
        if (!data) {
        scanInputRef.current?.focus();
        }
    }, [data]);

    // 더블탭 - 스캔 필드
    const handleScanDoubleTap = () => {
        const now = Date.now();
        if (now - lastTapTime < 300) {
        setScanKeyboardEnabled(true);
        scanInputRef.current?.focus();
        }
        setLastTapTime(now);
    };

    // 더블탭 - 저장위치 필드
    const handleLocationDoubleTap = () => {
        const now = Date.now();
        if (now - lastTapTime < 300) {
        setLocationKeyboardEnabled(true);
        locationInputRef.current?.focus();
        }
        setLastTapTime(now);
    };

    // Enter 키 처리
    const handleScanKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
        e.preventDefault();
        handleScanEnter();
        }
    };

    const handleLocationKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
        e.preventDefault();
        handleLocationEnter();
        }
    };

    return (
        <div className="w-full bg-white p-4 pb-2">
        <div className="space-y-1">
            
            {/* ━━━━━━━━━━ 스캔 입력 ━━━━━━━━━━ */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">SCAN</label>
            <div className="flex-1 flex gap-1">
                <input
                ref={scanInputRef}
                type="text"
                value={scanValue}
                onChange={(e) => setScanValue(e.target.value)}
                onKeyDown={handleScanKeyDown}
                onClick={handleScanDoubleTap}
                inputMode={scanKeyboardEnabled ? 'text' : 'none'}
                disabled={loading}
                placeholder="품목코드/바코드/LOT번호"
                autoComplete="off"
                className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                type="button"
                onClick={() => setScanKeyboardEnabled(!scanKeyboardEnabled)}
                className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                    scanKeyboardEnabled
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                </button>
            </div>
            </div>

            {/* ━━━━━━━━━━ 품목 정보 (읽기 전용) ━━━━━━━━━━ */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목코드</label>
            <input
                type="text"
                value={data?.itemCd || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            </div>

            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목명</label>
            <input
                type="text"
                value={data?.itemNm || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            </div>

            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">규격</label>
            <input
                type="text"
                value={data?.itemSpec || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            </div>

            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">품번</label>
            <input
                type="text"
                value={data?.modelNo || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            </div>

            {/* 현재고 + 단위 (한 행) */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">현재고</label>
            <input
                type="text"
                value={data?.stockQty?.toLocaleString() || ''}
                readOnly
                className="w-24 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <label className="text-lg font-medium text-black w-10 flex-shrink-0 ml-2">단위</label>
            <input
                type="text"
                value={data?.itemUnit || ''}
                readOnly
                className="w-16 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            </div>

            {/* 현재 저장위치 */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">저장위치</label>
            <input
                type="text"
                value={data?.locationCd || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            </div>

            {/* 구분선 */}
            <hr className="border-gray-300 my-2" />

            {/* ━━━━━━━━━━ 새 저장위치 입력 ━━━━━━━━━━ */}
            <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-22 flex-shrink-0">New위치</label>
            <div className="flex-1 flex gap-1">
                <input
                ref={locationInputRef}
                type="text"
                value={newLocationCd}
                onChange={(e) => setNewLocationCd(e.target.value)}
                onKeyDown={handleLocationKeyDown}
                onClick={handleLocationDoubleTap}
                inputMode={locationKeyboardEnabled ? 'text' : 'none'}
                disabled={loading || !data}
                placeholder="새 저장위치 스캔/입력"
                autoComplete="off"
                className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
                <button
                type="button"
                onClick={() => setLocationKeyboardEnabled(!locationKeyboardEnabled)}
                disabled={!data}
                className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                    locationKeyboardEnabled
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                </button>
            </div>
            </div>

            {/* ━━━━━━━━━━ 버튼 영역 ━━━━━━━━━━ */}
            <div className="flex gap-2 pt-2">
            <button
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 bg-gray-500 text-white px-4 py-2 rounded text-lg font-medium hover:bg-gray-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                취소
            </button>
            <button
                onClick={handleSave}
                disabled={loading || !data || !newLocationCd.trim()}
                className="flex-1 bg-blue-500 text-white px-4 py-2 rounded text-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                저장
            </button>
            </div>

        </div>
        </div>
    );
    };

    export default PDA1100_R01Info;
